import type {
  PurchaseOrderDto,
  PurchaseRequestDto,
  PurchaseSuggestionDto,
  ReceiptDto,
  PurchaseOrder,
  PoLine,
  PurchaseSuggestion,
  Receipt,
  UiVatRateStatus,
} from './types'
import { poLineSpecColor } from '@/features/purchasing/specColor'
import { formatSheetAmount } from '@/lib/sheetAmount'
import { normalizeServerNumber } from '@/components/table/freeSheetModel'

/** Tiền trên PO: tối đa 3 chữ số thập phân (4.696,25 ₫; 9.392,5 ₫; 188 ₫), không làm tròn số nguyên. */
export function money(val: number | null | undefined): string {
  if (val == null || !Number.isFinite(val)) return '—'
  return `${formatSheetAmount(val)} ₫`
}

export function formatRange(r?: { min: number; max: number } | { exact: number }): string {
  if (!r) return '—'
  if ('exact' in r) return `${r.exact}`
  return `${r.min}–${r.max}`
}

export type UiPurchaseOrder = PurchaseOrder & {
  subtotal?: number
  vatTotal?: number
  grandTotal?: number
}

export function poDtoToUi(dto: PurchaseOrderDto, materialsOrIndex?: Array<{ id: string; attributes?: Record<string, unknown> }> | number): UiPurchaseOrder {
  // Array.map passes an index as the second argument; only a catalog is used.
  const materials = Array.isArray(materialsOrIndex) ? materialsOrIndex : undefined
  return {
    id: dto.id,
    code: dto.code,
    supplierId: dto.supplier_id,
    supplierName: dto.supplier_name,
    status: dto.status,
    currency: dto.currency || 'VND',
    subtotal: dto.subtotal != null ? (normalizeServerNumber(dto.subtotal) ?? Number(dto.subtotal)) : undefined,
    vatTotal: dto.vat_total != null ? (normalizeServerNumber(dto.vat_total) ?? Number(dto.vat_total)) : undefined,
    grandTotal: dto.grand_total != null ? (normalizeServerNumber(dto.grand_total) ?? Number(dto.grand_total)) : undefined,
    vat_rate: dto.vat_rate == null ? null : (normalizeServerNumber(dto.vat_rate) ?? Number(dto.vat_rate)),
    deliveryDate: dto.delivery_date ?? '',
    deliverySite: dto.delivery_site ?? dto.shipping_address ?? undefined,
    createdAt: dto.created_at ? dto.created_at.slice(0, 10) : '',
    version: dto.version,
    incomingPosted: dto.status === 'APPROVED' || dto.status === 'SENT' || dto.status === 'PARTIAL',
    note: dto.note ?? dto.notes ?? undefined,
    approvedBy: dto.approved_by ?? undefined,
    approvedAt: dto.approved_at ?? undefined,
    approvedByRole: (dto.approved_by_role as any) ?? undefined,
    archivedAt: dto.archived_at ?? null,
    archivedBy: dto.archived_by ?? null,
    kind: dto.kind ?? 'STANDARD',
    freeLayout: dto.free_layout,
    freeLines: dto.free_lines,
    signers: dto.signers ?? undefined,
    lines: dto.kind === 'FREE' && Array.isArray(dto.free_lines)
      ? dto.free_lines.map((fl: any): PoLine => {
          const qty = normalizeServerNumber(fl.qty) ?? 0
          const receivedQty = normalizeServerNumber(fl.received_qty) ?? 0
          const unitPrice = normalizeServerNumber(fl.unit_price) ?? 0
          const lineTotal = fl.line_total != null
            ? (normalizeServerNumber(fl.line_total) ?? 0)
            : Math.round((qty * unitPrice + Number.EPSILON) * 100) / 100
          return {
            id: fl.id,
            materialId: fl.id,
            itemCode: fl.id,
            itemName: fl.name,
            spec: '',
            spec_attributes: null,
            uom: fl.uom,
            qty,
            receivedQty,
            shortQty: 0,
            shortResolution: null,
            incomingOpenQty: Math.max(0, qty - receivedQty),
            unitPriceSystem: unitPrice,
            unitPriceActual: unitPrice,
            vatRateStatus: 'KNOWN',
            vat: 0,
            currency: dto.currency || 'VND',
            priceSource: 'MANUAL',
            lineTotal,
          }
        })
      : (dto.lines ?? []).map((l): PoLine => {
      const vatStatus: UiVatRateStatus =
        l.vat_rate_status === 'UNKNOWN' ? 'UNKNOWN' : 'KNOWN'
      const sc = poLineSpecColor(l, materials?.find(m => m.id === l.material_id)?.attributes)
      return {
        id: l.id,
        materialId: l.material_id,
        itemCode: l.material_code ?? l.material_id,
        itemName: String(l.price_snapshot?.input?.material_name ?? l.material_name ?? '—'),
        spec: sc.spec,
        spec_attributes: { ...((l.price_snapshot?.input?.spec_attributes as Record<string, unknown> | null) ?? l.spec_attributes), color: sc.color },
        priceSnapshot: l.price_snapshot ?? null,
        priceBasisQty: Number(l.price_basis_qty ?? 1),
        stockUom: l.stock_uom ?? null,
        priceToStockFactor: l.price_to_stock_factor == null ? null : Number(l.price_to_stock_factor),
        billableQty: Number(l.billable_qty),
        thick: l.thick ?? undefined,
        width: l.width ?? undefined,
        length: l.length ?? undefined,
        height: l.height ?? undefined,
        uom: l.order_uom as any,
        qty: Number(l.order_qty),
        receivedQty: Number(l.received_qty ?? 0),
        shortQty: Number(l.short_qty ?? 0),
        shortResolution: l.short_resolution ?? null,
        incomingOpenQty: Math.max(
          0,
          Number(l.order_qty) - Number(l.received_qty ?? 0) - Number(l.short_qty ?? 0),
        ),
        unitPriceSystem: l.unit_price_system != null ? Number(l.unit_price_system) : 0,
        unitPriceActual: Number(l.unit_price_actual),
        vatRateStatus: vatStatus,
        vat: l.vat_rate_value != null ? Number(l.vat_rate_value) : null,
        currency: dto.currency || 'VND',
        priceSource: l.price_source as any,
        priceRuleId: l.price_rule_id ?? undefined,
        priceRuleLabel: l.price_rule_label ?? undefined,
        priceUom: l.price_uom ?? undefined,
        manualReason: l.manual_reason ?? undefined,
        requirementLineId: l.requirement_line_id ?? undefined,
        lineTotal: Number(l.line_total),
      }
    }),
    editedAfterApprovalAt: dto.edited_after_approval_at ?? null,
    editCount: dto.edit_count ?? 0,
  }
}

export type UiPrLine = {
  id?: string
  materialId: string
  materialCode?: string
  materialName?: string
  qty: number
  uom: any
  note?: string
}

export type UiPurchaseRequest = {
  id: string
  code: string
  status: string
  department?: string | null
  requestedBy?: string
  createdAt: string
  lines: UiPrLine[]
}

export function prDtoToUi(dto: PurchaseRequestDto): UiPurchaseRequest {
  return {
    id: dto.id,
    code: dto.code,
    status: dto.status,
    department: dto.department,
    requestedBy: dto.requested_by,
    createdAt: dto.created_at ? dto.created_at.slice(0, 10) : '',
    lines: (dto.lines ?? []).map((l) => ({
      id: l.id,
      materialId: l.material_id,
      materialCode: l.material_code,
      materialName: l.material_name,
      qty: Number(l.order_qty),
      uom: l.order_uom as any,
      note: l.notes ?? undefined,
    })),
  }
}

export function sugDtoToUi(dto: PurchaseSuggestionDto): PurchaseSuggestion & {
  code?: string
  name?: string
  uom: string
} {
  return {
    id: dto.id,
    materialId: dto.material_id,
    code: dto.material_code,
    name: dto.material_name,
    policy: 'Min/Max',
    available: Number(dto.current_stock ?? 0),
    incoming: Number(dto.incoming_stock ?? 0),
    projected: Number(dto.current_stock ?? 0) + Number(dto.incoming_stock ?? 0),
    suggestedQty: Number(dto.suggested_qty),
    uom: dto.uom,
    preferredSupplierId: dto.suggested_supplier_id ?? undefined,
    leadTimeDays: 7,
    needByDate: dto.created_at ? dto.created_at.slice(0, 10) : '',
    orderByDate: dto.created_at ? dto.created_at.slice(0, 10) : '',
    reason: dto.reason,
    status: dto.status as any,
  }
}

export function receiptDtoToUi(dto: ReceiptDto): Receipt {
  return {
    id: dto.id,
    code: dto.code,
    poId: dto.po_id,
    at: dto.created_at ? dto.created_at.slice(0, 10) : '',
    by: dto.received_by,
    lines: (dto.lines ?? []).map((l) => ({
      poLineId: l.po_line_id,
      qty: Number(l.qty_order_uom),
    })),
  }
}
